import { Container, Tabs, Tab, Box, CircularProgress, Typography, Backdrop } from '@mui/material';
import { useState } from 'react';
import ModalCorreccion from './components/ModalCorreccion';
import HistorialCorrecciones from './components/HistorialCorrecciones';
import { useInventario } from './hooks/useInventario';
import { useInventarioSaving } from './services/inventarioApi';
import HistorialMovimientos from './HistorialMovimientos';
import ConfigInicial from './ConfigInicial';
import Dashboard from './Dashboard';
import DashboardGanancias from './DashboardGanancias';
import { useSubscriptionStatus } from '../useSubscriptionStatus';
import SubscriptionGuard from '../SubscriptionGuard';
import HistorialGastos from './HistorialGastos';
import { useGastos } from './hooks/useGastos';
import { useLimpiezas } from './hooks/useLimpiezas';
import ArqueoCaja from './ArqueoCaja';
import TraspasoCaja from './TraspasoCaja';
import HistorialAjustes from './HistorialAjustes';
import Reportes from './Reportes';
import GeneralDelDia from './GeneralDelDia';
import InventarioLandingPage from './InventarioLandingPage';
import { useAuthStore } from '../../store/authStore';
import { createDefaultInventarioFilters, createDefaultStockProductFilter } from './utils/inventarioFilters';

export default function InventarioApp() {
  const [tab, setTab] = useState(0);
  const saving = useInventarioSaving(state => state.pending > 0);
  const [kardexFilters, setKardexFilters] = useState(createDefaultInventarioFilters);
  const [stockFilters, setStockFilters] = useState(createDefaultStockProductFilter);
  const gastosState = useGastos();
  const state = useInventario(gastosState);
  const { status, daysLeft, expiresAtLabel } = useSubscriptionStatus('inventario');
  const [origenCorreccion, setOrigenCorreccion] = useState(null);
  const [historialCorrecciones, setHistorialCorrecciones] = useState(false);
  const [historialFocus, setHistorialFocus] = useState(null);
  const limpiezasState = useLimpiezas({
    onRefreshMovimientos: () => state.refetchMovimientos?.(),
    onRefreshGastos: () => gastosState.refetch?.(),
    setMovimientos: state.setMovimientos,
    obtenerPrecioPromedioHasta: state.obtenerPrecioPromedioHasta,
    marcarGastoConvertidoLocal: gastosState.marcarGastoConvertidoALimpiezaLocal,
  });
  const { isAuthenticated } = useAuthStore();

  const refreshCorreccion = async () => {
    await Promise.all([state.refetchTodo(), limpiezasState.fetchLimpiezas()]);
  };
  const corregirTransaccion = (t, modo = 'CONVERTIR') => {
    const datos = t.objOriginal || t.datos || t;
    if (datos.origenOperacion === 'LIMPIEZA' || datos.operacionId) {
      const proceso = limpiezasState.limpiezas.find(l => String(l.id || l._id) === String(datos.operacionId) || l.transactionID === datos.transactionID);
      if (!proceso) { window.alert('No se encontró la limpieza completa. Actualiza los datos.'); return; }
      setOrigenCorreccion({ tipo: 'LIMPIEZA', datos: proceso, modo }); return;
    }
    const tipo = t.tipo === 'MOVIMIENTO' ? (t.subtipo === 'ENTRADA' ? 'COMPRA' : t.subtipo) : t.tipo;
    setOrigenCorreccion({ tipo, datos, modo });
  };
  const mostrarHistorial = focus => { setHistorialFocus(focus || null); setHistorialCorrecciones(true); };

  if (state.migrating) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 2 }}>
      <CircularProgress size={48} />
      <Typography variant="h6">Migrando datos al servidor...</Typography>
      <Typography variant="body2" color="text.secondary">Esto solo ocurre una vez. No cierres la aplicación.</Typography>
    </Box>
  );

  if (!state.metodo && !state.configLoading && isAuthenticated) return <ConfigInicial seleccionarMetodo={state.seleccionarMetodo} />;

  return (
    <SubscriptionGuard status={status} daysLeft={daysLeft} expiresAtLabel={expiresAtLabel} landing={<InventarioLandingPage />} appKey={"inventario"} appName={"Inventario y ventas"}>
          <Backdrop open={state.configLoading || saving} sx={{ zIndex: 9999, flexDirection: 'column', gap: 2, color: '#fff', backdropFilter: 'blur(4px)' }}>
            <CircularProgress color="inherit" size={56} thickness={4} />
            <Typography variant="h6" fontWeight={600}>{saving ? 'Guardando...' : 'Cargando inventario...'}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>{saving ? 'Espera un momento' : 'Sincronizando con el servidor'}</Typography>
          </Backdrop>
          <Container maxWidth={false} disableGutters sx={{ mt: 4, pb: 5, width: '100%' }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth" sx={{ mb: 3 }}>
              <Tab label="🎯 General del Día" />
              <Tab label="Cargar & Kardex" />
              <Tab label="Inventario" />
              <Tab label="Ganancias" />
              <Tab label="Gastos" />
              <Tab label="Arqueo de Caja" />
              <Tab label="Traspasos" />
              <Tab label="Historial de Ajustes" />
              <Tab label="Reportes" />
              {/* <Tab label="📒 Libro Diario" /> */}
            </Tabs>
            {tab === 0 && (
              <GeneralDelDia
                state={state}
                gastosState={gastosState}
                limpiezasState={limpiezasState}
                onConvertirALimpieza={(g) => corregirTransaccion({ tipo: 'GASTO', objOriginal: g })}
                onCorregir={isAuthenticated ? corregirTransaccion : undefined}
                onHistorialCorrecciones={isAuthenticated ? mostrarHistorial : undefined}
              />
            )}
            {tab === 1 && (
              <HistorialMovimientos
                state={state}
                movimientos={state.movimientos}
                productos={state.productos}
                anularMovimiento={state.anularMovimiento}
                filters={kardexFilters}
                onFiltersChange={setKardexFilters}
              />
            )}
            {tab === 2 && (
              <Dashboard
                state={state}
                filters={stockFilters}
                onFiltersChange={setStockFilters}
              />
            )}
            {tab === 3 && <DashboardGanancias state={state} />}
            {tab === 4 && 
              <HistorialGastos
                state={state}
                gastos={gastosState.gastos}
                editarGasto={gastosState.editarGasto}
                categorias={gastosState.categorias}
                registrarGasto={gastosState.registrarGasto}
                anularGasto={gastosState.anularGasto}
                agregarCategoria={gastosState.agregarCategoria}
                onConvertirALimpieza={(g) => corregirTransaccion({ tipo: 'GASTO', objOriginal: g })}
                onAfterSync={async () => {
                  await gastosState.refetch?.();
                  await state.refetchMovimientos?.();
                  await limpiezasState.fetchLimpiezas?.();
                }}
              />
            }
            {tab === 5 && <ArqueoCaja state={state} gastos={gastosState.gastos} />}
            {tab === 6 && <TraspasoCaja state={state} />}
            {tab === 7 && <HistorialAjustes state={state} />}
            {tab === 8 && <Reportes state={state} gastosState={gastosState} />}
            {/* {tab === 9 && <LibroDiario state={state} gastosState={gastosState} />} */}
          <ModalCorreccion origen={origenCorreccion} onClose={() => setOrigenCorreccion(null)} onSaved={refreshCorreccion} productos={state.productos} categorias={gastosState.categorias} />
          <HistorialCorrecciones open={historialCorrecciones} onClose={() => setHistorialCorrecciones(false)} onSaved={refreshCorreccion} productos={state.productos} categorias={gastosState.categorias} focusId={historialFocus} />
          </Container>
    </SubscriptionGuard>
    
  );
}